import Box from '@mui/material/Box'
import { BuildingViewMode } from '../../../models/Construction'
import { StackableViewIcon, WholeBuildingIcon } from '../../Svgs'
import {
  MenuItem,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../store'
import {
  setSelectedBuildingMode,
  setSelectedStoreyId,
} from '../../../actions/construction'
import CustomDropdown from '../../CustomDropdown'
import { createSelector } from '@reduxjs/toolkit'
import { formatOrdinals } from '../../../utils/Common'
import { useCallback, useMemo } from 'react'

interface BuildingModesProps {
  isDisabled?: boolean
  reversed?: boolean
}

const buildingModes = [
  {
    mode: BuildingViewMode.StackableView,
    svg: StackableViewIcon,
  },
  {
    mode: BuildingViewMode.WholeBuilding,
    svg: WholeBuildingIcon,
  },
]

type StoreyListItem = {
  id: string | null
  name: string
}

const selectDropdownStoreyList = createSelector(
  (state: RootState) => state.construction.convertData.building.storeys,
  (storeys) => {
    const storeyList: StoreyListItem[] = [
      {
        id: null,
        name: 'Entire Home',
      },
    ]

    storeys.forEach((storey, index) => {
      storeyList.push({
        id: storey.id,
        name: `${formatOrdinals(index + 1)} Floor`,
      })
    })

    return storeyList
  },
)

const BuildingViewModes: React.FC<BuildingModesProps> = ({
  isDisabled,
  reversed = false,
}) => {
  const dispatch = useDispatch()
  const storeyList = useSelector(selectDropdownStoreyList)
  const selectedBuildingMode = useSelector((state: RootState) => {
    const selectedBuildingMode = state.construction.selectedBuildingMode
    return selectedBuildingMode === BuildingViewMode.SingleStorey
      ? BuildingViewMode.StackableView
      : selectedBuildingMode
  })

  const selectedStoreyId = useSelector(
    (state: RootState) => state.construction.selectedStoreyId,
  )
  const handleBuildingModeChange = useCallback(
    (mode: BuildingViewMode) => {
      if (mode === BuildingViewMode.WholeBuilding) {
        dispatch(setSelectedStoreyId(null))
        dispatch(setSelectedBuildingMode({ mode }))
      } else {
        dispatch(
          setSelectedBuildingMode({
            mode: selectedStoreyId
              ? BuildingViewMode.SingleStorey
              : BuildingViewMode.StackableView,
          }),
        )
      }
    },
    [selectedStoreyId, dispatch],
  )

  const handleItemClick = useCallback(
    (id: string | null) => {
      dispatch(setSelectedStoreyId(id))
      dispatch(
        setSelectedBuildingMode({
          mode: id
            ? BuildingViewMode.SingleStorey
            : BuildingViewMode.StackableView,
        }),
      )
    },
    [dispatch],
  )

  const dropdownTitle = useMemo(() => {
    return storeyList.find((item) => item.id === selectedStoreyId)?.name ?? ''
  }, [storeyList, selectedStoreyId])

  const dropdownItems = useMemo(() => {
    return storeyList
      .filter((item) => item.id !== selectedStoreyId)
      .map((item) => (
        <MenuItem key={item.id} onClick={() => handleItemClick(item.id)}>
          <Typography
            sx={{
              color: '#04002E',
              fontSize: '12px',
              textTransform: 'capitalize',
            }}
          >
            {item.name}
          </Typography>
        </MenuItem>
      ))
  }, [storeyList, selectedStoreyId, handleItemClick])

  return (
    <Stack
      direction={reversed ? 'row-reverse' : 'row'}
      columnGap={'4px'}
      sx={{
        height: '32px',
        pointerEvents: 'auto',
      }}
    >
      <ToggleButtonGroup
        size='small'
        color='primary'
        value={selectedBuildingMode}
        exclusive
        aria-label='Platform'
        sx={{
          borderRadius: '4px',
          backgroundColor: '#F2F2F2',
          width: '8.5rem',
          '.MuiToggleButton-root': {
            margin: 0,
            border: 'none',
            borderRadius: '4px',
          },
        }}
      >
        {buildingModes.map((bm, index) => (
          <ToggleButton
            disabled={isDisabled}
            key={index}
            value={bm.mode}
            onClick={() => handleBuildingModeChange(bm.mode)}
            sx={{
              width: '100%',
              backgroundColor: '#F2F2F2',
              '&:hover': {
                backgroundColor: '#E5F0FF',
              },
              '&.Mui-selected': {
                backgroundColor: '#1705E5',
                '&:hover': {
                  backgroundColor: '#1705E5',
                },
              },
            }}
          >
            <bm.svg
              background={
                selectedBuildingMode === bm.mode ? '#3F69FF' : '#C6C6C6'
              }
              color={selectedBuildingMode === bm.mode ? 'white' : 'black'}
            />
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      <Box
        sx={{
          display: 'flex',
          background: '#F2F2F2',
          borderRadius: '4px',
          padding: 0,
          width: '10rem',
        }}
      >
        <CustomDropdown
          disabled={
            isDisabled ||
            selectedBuildingMode === BuildingViewMode.WholeBuilding
          }
          title={dropdownTitle}
          withoutOpacity={true}
          color='#06004A'
          buttonStyleProps={{
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {dropdownItems}
        </CustomDropdown>
      </Box>
    </Stack>
  )
}

export default BuildingViewModes
